import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { Banknote, Loader2, Search, Check, X, ArrowRight, Clock, Minus } from "lucide-react";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const START_YEAR = 2026;

const Cell = ({ status, testId }) => {
    if (status === "ok") {
        return <td className="px-1 py-1.5 text-center" data-testid={testId}><Check className="mx-auto h-3.5 w-3.5 text-emerald-500" /></td>;
    }
    if (status === "missing") {
        return <td className="bg-red-50 px-1 py-1.5 text-center" data-testid={testId}><X className="mx-auto h-3.5 w-3.5 text-red-500" /></td>;
    }
    if (status === "future") {
        return (
            <td className="px-1 py-1.5 text-center" data-testid={testId} title="Non ancora emessa">
                <Clock className="mx-auto h-3.5 w-3.5 text-amber-400" />
            </td>
        );
    }
    return (
        <td className="px-1 py-1.5 text-center" data-testid={testId} title="Non prevista / non in forza">
            <span className="mx-auto inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200">
                <Minus className="h-2.5 w-2.5 text-slate-500" />
            </span>
        </td>
    );
};

export default function PayslipCheckPage() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let y = Math.max(START_YEAR, currentYear); y >= START_YEAR; y--) years.push(y);
    const [year, setYear] = useState(years[0]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onlyMissing, setOnlyMissing] = useState(true);
    const [search, setSearch] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get("/documents/payslips/report", { params: { year } })
            .then((r) => setData(r.data))
            .catch(() => toast.error("Errore nel caricamento del report"))
            .finally(() => setLoading(false));
    }, [year]);

    const rows = useMemo(() => {
        if (!data) return [];
        let list = data.employees;
        if (onlyMissing) list = list.filter((e) => e.missing > 0);
        const q = search.trim().toLowerCase();
        if (q) list = list.filter((e) => `${e.cognome} ${e.nome} ${e.matricola}`.toLowerCase().includes(q));
        return list;
    }, [data, onlyMissing, search]);

    const t = data?.totals;

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="payslip-check-page">
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Banknote className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Controllo buste paga</h1>
                        <p className="text-sm text-slate-500">
                            12 mensilità + XIII e XIV per ogni dipendente, in base al periodo di lavoro
                        </p>
                    </div>
                </div>
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                    <SelectTrigger className="h-9 w-28 text-sm" data-testid="payslip-year-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {t && (
                <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="payslip-kpi-checked">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Dipendenti controllati</div>
                        <div className="font-mono-numbers text-xl font-bold text-slate-800">{t.checked}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="payslip-kpi-with-missing">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Con buste mancanti</div>
                        <div className={`font-mono-numbers text-xl font-bold ${t.with_missing > 0 ? "text-red-600" : "text-emerald-600"}`}>{t.with_missing}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="payslip-kpi-monthly">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Mensilità mancanti</div>
                        <div className={`font-mono-numbers text-xl font-bold ${t.monthly > 0 ? "text-red-600" : "text-emerald-600"}`}>{t.monthly}</div>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="payslip-kpi-bonus">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">XIII / XIV mancanti</div>
                        <div className={`font-mono-numbers text-xl font-bold ${t.xiii + t.xiv > 0 ? "text-red-600" : "text-emerald-600"}`}>{t.xiii} / {t.xiv}</div>
                    </div>
                </div>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <div className="relative min-w-[220px] flex-1">
                    <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca dipendente o matricola…"
                        className="h-9 w-full rounded-md border border-slate-200 bg-white pl-8 pr-3 text-sm text-slate-700"
                        data-testid="payslip-search-input"
                    />
                </div>
                <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                    <Switch checked={onlyMissing} onCheckedChange={setOnlyMissing} data-testid="payslip-only-missing-toggle" />
                    Solo con mancanze
                </label>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-300" />
                </div>
            ) : rows.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="payslip-empty">
                    {onlyMissing ? "Nessun dipendente con buste paga mancanti. Tutto in regola!" : "Nessun dipendente da controllare per questo anno."}
                </div>
            ) : (
                <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                <th className="sticky left-0 z-10 bg-slate-50 px-3 py-2.5">Dipendente</th>
                                {MONTHS.map((m) => <th key={m} className="px-1 py-2.5 text-center">{m}</th>)}
                                <th className="px-1.5 py-2.5 text-center">XIII</th>
                                <th className="px-1.5 py-2.5 text-center">XIV</th>
                                <th className="px-3 py-2.5 text-right">Mancanti</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((e) => (
                                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`payslip-row-${e.id}`}>
                                    <td className="sticky left-0 z-10 whitespace-nowrap bg-white px-3 py-1.5">
                                        <Link to={`/dipendenti/${e.id}`} className="flex items-center gap-1.5 font-semibold text-slate-700 hover:text-[#376DB3] hover:underline">
                                            {e.cognome} {e.nome}
                                            <ArrowRight className="h-3 w-3 text-slate-300" />
                                        </Link>
                                        <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                                            {e.matricola && <span>matr. {e.matricola}</span>}
                                            {!e.active && (
                                                <span className="rounded-full bg-slate-100 px-1.5 py-px font-semibold text-slate-500">
                                                    cessato {e.data_cessazione ? new Date(e.data_cessazione).toLocaleDateString("it-IT") : ""}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    {MONTHS.map((_, i) => (
                                        <Cell key={`month-${i + 1}`} status={e.months[String(i + 1)]} testId={`payslip-cell-${e.id}-${i + 1}`} />
                                    ))}
                                    <Cell status={e.xiii} testId={`payslip-cell-${e.id}-xiii`} />
                                    <Cell status={e.xiv} testId={`payslip-cell-${e.id}-xiv`} />
                                    <td className="px-3 py-1.5 text-right">
                                        {e.missing > 0 ? (
                                            <span className="font-mono-numbers font-bold text-red-600">{e.missing}</span>
                                        ) : (
                                            <Check className="ml-auto h-3.5 w-3.5 text-emerald-500" />
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-500" /> presente</span>
                <span className="flex items-center gap-1"><X className="h-3.5 w-3.5 text-red-500" /> mancante</span>
                <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-amber-400" /> non ancora emessa</span>
                <span className="flex items-center gap-1">
                    <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-slate-200"><Minus className="h-2.5 w-2.5 text-slate-500" /></span>
                    non prevista / non in forza
                </span>
            </div>
        </div>
    );
}
